import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { config } from '../../config';
import { canCapture, commitIncrement, prepareIncrement } from '../../app/discovery';
import { loadLockedBoard, setTileOverride } from '../../app/lockedBoard';
import type { BoardTileView } from '../../app/lockedBoard';
import { log } from '../../app/logger';
import { applyLiveIncrement, setStatusOverride } from '../../domain/counts';
import { boardSlots, nextBoardOrder, swipeLeftOverride } from '../../domain/lockedBoard';
import { reorderAllowed } from '../../domain/reorder';
import type { QuietGate } from '../../domain/reorder';
import type { IncrementCommand } from '../../domain/commands';
import type { MatchRow } from '../../types';
import { registerSettleTriggers } from '../../ui/capture/settleTriggers';
import type { CaptureIntent, GestureTarget } from '../../ui/capture/usePointerGesture';
import type { UndoRowB } from './bandB';

/**
 * B-10 — the locked board's controller (design spec §2.4; scenario 12): state, the tap path,
 * the swipe, and the deferred-reflow engine's glue. No rendering.
 *
 * Every **decision** is imported from its one engine home, per the reuse contract:
 * `nextBoardOrder`/`boardSlots` (INV-11's derived order + LOCK-7's grid under DISC-7's quiet
 * gate — `domain/lockedBoard.ts` is the one entry point that decides render order),
 * `swipeLeftOverride` (what a swipe leaves on a tile), `prepareIncrement`/`commitIncrement`
 * (the one tap path both capture surfaces walk — the same `Increment` whose op set cannot
 * compile a position write, which is why scenario 12's "nothing relocates in response to
 * counts" needs no code here to hold it), `applyLiveIncrement`/`setStatusOverride` (the same
 * folds the handlers ran, applied locally after the commit — never a per-tap re-read),
 * `registerSettleTriggers` (the shipped trigger set). What is B's own is React glue: one gate
 * ref, one settle timer — the same shape A's `useLockedBoard` proved (its module note explains
 * every choice this file repeats), plus B-15's long-press seam onto the hypothesis detail sheet,
 * minus what other B units own (the undo pill is **B-11's** bottom band; halftime board manage
 * is **B-14's**).
 *
 * ── The match row is the shell's; the tiles are this hook's ──────────────────
 *
 * `TakeoverShell` owns the one authoritative match row (re-read after every committed
 * transition) and passes it down — a second copy of the row here could disagree with the
 * chrome about the state. The tiles are read **once, at mount**, through `loadLockedBoard`
 * (the read deliberately without the observation ledger — baseline: the board has no count
 * term in its order, so the ledger is not in its transaction), then folded locally per commit.
 * Transitions never change tile rows, and the one mid-match write that can (B-14's halftime
 * swap) happens while this surface is unmounted for the break hub — the remount re-reads.
 */

export type LockedBoardPhaseB = 'loading' | 'ready' | 'failed';

interface Board {
  readonly tiles: readonly BoardTileView[];
  readonly order: readonly string[];
}

const MODULE = 'ui-b/lockedBoard';

/**
 * B-11's additive seams (the same pair Discovery's hook carries, for the same band):
 * `onRowsChanged` publishes the committed tile state; `refreshEpoch` orders a re-read after a
 * band undo commits. Both optional — without them the hook behaves exactly as before.
 */
export interface LockedBoardSeamsB {
  readonly onRowsChanged?: (rows: readonly UndoRowB[]) => void;
  readonly refreshEpoch?: number;
}

export function useLockedBoardB(match: MatchRow, seams: LockedBoardSeamsB = {}) {
  const { onRowsChanged, refreshEpoch } = seams;
  const [phase, setPhase] = useState<LockedBoardPhaseB>('loading');
  const [board, setBoard] = useState<Board>({ tiles: [], order: [] });
  const [integrity, setIntegrity] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * B-15 — the tile the detail sheet is open on, or `null`. Held here rather than in the
   * surface because the 450 ms hold that opens it is one of this hook's gesture callbacks.
   */
  const [detailId, setDetailId] = useState<string | null>(null);
  const loaded = useRef(false);

  /**
   * DISC-7's gate, in a **ref** — A's documented reason holds unchanged: a timer callback
   * reads it and pointer handlers write it, and a stale closure over rendered state is exactly
   * how a settle would be judged against a tap that has already happened.
   */
  const gate = useRef<QuietGate>({ lastTapTs: null, pointerDown: false });
  const settleTimer = useRef<number | undefined>(undefined);

  const applyTiles = useCallback((tiles: readonly BoardTileView[]): void => {
    setBoard((prev) => ({
      tiles,
      order: nextBoardOrder(tiles, prev.order, gate.current, Date.now()),
    }));
  }, []);

  /** `scheduleSettle` re-arms itself; the ref breaks the definition cycle (A's device). */
  const scheduleSettleRef = useRef<() => void>(() => undefined);

  /**
   * The settle scheduler — the only code that applies a deferred reorder, always by asking
   * `reorderAllowed` against the same gate (re-evaluate, never bypass). What it defers on this
   * surface is the *status*-driven re-rank LIVE-3 requires (a disproved tile sinking, an
   * un-disproved tile floating back to its exact slot); a count can never move a tile because
   * `nextBoardOrder`'s parameter type declares no count at all.
   */
  const scheduleSettle = useCallback((): void => {
    if (settleTimer.current !== undefined) window.clearTimeout(settleTimer.current);
    settleTimer.current = undefined;
    // A finger is down: shut for a reason no timeout can wait out. The release path re-schedules.
    if (gate.current.pointerDown) return;

    const last = gate.current.lastTapTs;
    const delay = last === null ? 0 : Math.max(0, last + config.REORDER_QUIET_MS - Date.now());
    settleTimer.current = window.setTimeout(() => {
      settleTimer.current = undefined;
      setBoard((prev) => ({
        tiles: prev.tiles,
        order: nextBoardOrder(prev.tiles, prev.order, gate.current, Date.now()),
      }));
      // Re-arm when the gate was still shut — a settle refused with no follow-up is a board
      // that never applies LIVE-3's re-rank again. Asked of `reorderAllowed`, never re-derived.
      if (!reorderAllowed(gate.current, Date.now())) scheduleSettleRef.current();
    }, delay);
  }, []);

  scheduleSettleRef.current = scheduleSettle;

  useEffect(
    () => () => {
      if (settleTimer.current !== undefined) window.clearTimeout(settleTimer.current);
    },
    [],
  );

  /** DISC-7's lifecycle triggers — the shipped set, shared with every capture surface. */
  useEffect(() => registerSettleTriggers(scheduleSettle), [scheduleSettle]);

  // ── Load (once at mount — see the header for why transitions need no re-read) ─

  /** The one read this hook performs — at mount, and again on B-11's undo epoch. */
  const refresh = useCallback(async (): Promise<void> => {
    const context = await loadLockedBoard(match.id);
    // A vanished match is the shell's boundary to handle (its own next read navigates);
    // this surface simply reports that it could not render a board.
    if (context === undefined) {
      setPhase('failed');
      return;
    }
    setIntegrity(context.integrity);
    applyTiles(context.tiles);
    setPhase('ready');
  }, [applyTiles, match.id]);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    refresh().catch((cause: unknown) => {
      log.exception(MODULE, 'the locked board could not be loaded', cause);
      setPhase('failed');
    });
  }, [refresh]);

  // ── B-11's seams: publish committed tiles up; re-read on the band's undo epoch ─

  /** Published on every committed change (load, fold, override) — the pill's freshness. */
  useEffect(() => {
    onRowsChanged?.(board.tiles);
  }, [board.tiles, onRowsChanged]);

  /**
   * A band undo committed: re-read and republish (the undo rewrote `lastIncrementTs`, and the
   * count/status this grid shows, in the store). Guarded by the last-seen epoch so the other
   * dependency can never trigger a spurious read.
   */
  const seenEpoch = useRef(refreshEpoch ?? 0);
  useEffect(() => {
    const epoch = refreshEpoch ?? 0;
    if (epoch === seenEpoch.current) return;
    seenEpoch.current = epoch;
    refresh().catch((cause: unknown) => {
      const message = cause instanceof Error ? cause.message : String(cause);
      setNotice(message);
      log.error(MODULE, 'the board could not be re-read after an undo', { message });
    });
  }, [refreshEpoch, refresh]);

  // ── The tap path (§5.1; scenario 8 on the board) ───────────────────────────

  /**
   * Pointerdown: pre-build the whole command (id minted, minute derived) so no settle or
   * re-render between down and up can retarget the tap. Returning `null` declines the contact
   * — B keeps A's `canCapture` UI gate (the engine deliberately has no state guard on
   * `Increment`; "taps before kickoff create nothing" is this line). An **empty slot never
   * reaches here at all**: it carries no `data-hid`, so `resolveTarget` finds nothing and the
   * gesture layer returns before this is called (LOCK-7's mechanism).
   */
  const onPressStart = useCallback(
    (target: GestureTarget, at: number): CaptureIntent<IncrementCommand> | null => {
      if (!canCapture(match)) return null;
      target.element.dataset.pressed = 'true';
      return { kind: 'increment', command: prepareIncrement({ match, hypothesisId: target.hid, at }) };
    },
    [match],
  );

  const onPressEnd = useCallback((target: GestureTarget): void => {
    delete target.element.dataset.pressed;
  }, []);

  const onActiveChange = useCallback(
    (pointerDown: boolean): void => {
      gate.current = { ...gate.current, pointerDown };
      if (!pointerDown) scheduleSettle();
    },
    [scheduleSettle],
  );

  /**
   * B-15 — §2.4's *"long-press → hypothesis detail sheet"*, on the board exactly as on a
   * Discovery row. The hold fires from the gesture layer's timer while the finger is down and
   * the contact then classifies as `longPress`, so the press that opens the sheet can neither
   * increment nor swipe. An empty slot cannot reach here (no `data-hid`).
   */
  const onLongPress = useCallback((target: GestureTarget): void => {
    setDetailId(target.hid);
  }, []);

  /**
   * Pointerup on a completed tap: dispatch the pre-built command; the **only** acknowledgment
   * is `onCommitted`, invoked synchronously inside the native IndexedDB `complete` listener
   * (SES-2 — no optimistic path). The flash is applied imperatively on the element captured at
   * `pointerdown`: identity, not position. LIVE-11 needs no branch here — `applyLiveIncrement`
   * clears any dismissal (the same fold the handler ran) and the tile floats back among the
   * active tiles on the next settle, never sooner.
   */
  const onTap = useCallback(
    (target: GestureTarget, intent: CaptureIntent<IncrementCommand>, at: number): void => {
      gate.current = { ...gate.current, lastTapTs: at };
      scheduleSettle();
      if (intent.kind !== 'increment') return;
      const command = intent.command;

      void commitIncrement(command, () => {
        // Scenario 8's unmissable ack, at the commit moment. Remove → reflow → set, so a
        // second tap inside ACK_PERSIST_MIN_MS restarts the animation instead of showing nothing.
        const element = target.element;
        delete element.dataset.ack;
        void element.offsetWidth;
        element.dataset.ack = 'true';
        window.setTimeout(() => {
          delete element.dataset.ack;
        }, config.ACK_PERSIST_MIN_MS);
      }).then(
        (result) => {
          if (!result.ok) {
            setNotice(result.message);
            return;
          }
          setBoard((prev) => {
            const tiles = prev.tiles.map((tile) =>
              tile.id === command.hypothesisId
                ? { ...tile, ...applyLiveIncrement(tile, command.observation.createdAt) }
                : tile,
            );
            return { tiles, order: nextBoardOrder(tiles, prev.order, gate.current, Date.now()) };
          });
        },
        (cause: unknown) => {
          const message = cause instanceof Error ? cause.message : String(cause);
          setNotice(message);
          log.error(MODULE, 'an increment failed and was rolled back', { message });
        },
      );
    },
    [scheduleSettle],
  );

  /**
   * Scenario 12/LIVE-5 — swipe-left cycles the status override; the same gesture reverses it.
   * The value is decided by `domain/lockedBoard.swipeLeftOverride`, never here. The tile
   * **restyles in place immediately** (de-emphasis is the feedback that the swipe landed) and
   * only its *position* waits for the quiet window — the gate is shut on the swipe exactly as
   * on a tap, because correcting a mis-swipe is the double-action most likely to land on the
   * wrong tile if the board settled under it.
   */
  const onSwipeLeft = useCallback(
    (target: GestureTarget, at: number): void => {
      if (!canCapture(match)) return;
      const tile = board.tiles.find((row) => row.id === target.hid);
      if (tile === undefined) return;

      gate.current = { ...gate.current, lastTapTs: at };
      scheduleSettle();

      const next = swipeLeftOverride(tile.statusOverride);
      void setTileOverride(match.id, tile.id, next).then(
        (result) => {
          if (!result.ok) {
            setNotice(result.message);
            return;
          }
          setBoard((prev) => {
            const tiles = prev.tiles.map((row) =>
              row.id === tile.id ? { ...row, ...setStatusOverride(row, next) } : row,
            );
            return { tiles, order: nextBoardOrder(tiles, prev.order, gate.current, Date.now()) };
          });
        },
        (cause: unknown) => {
          const message = cause instanceof Error ? cause.message : String(cause);
          setNotice(message);
          log.error(MODULE, 'a swipe-left could not be committed', { message });
        },
      );
    },
    [board.tiles, match, scheduleSettle],
  );

  const slots = useMemo(() => boardSlots(board.tiles, board.order), [board]);

  /** B-15 — the tile the detail sheet renders, re-derived per render so its count stays live. */
  const detailTile = useMemo(
    () => (detailId === null ? null : (board.tiles.find((tile) => tile.id === detailId) ?? null)),
    [board.tiles, detailId],
  );

  return {
    state: {
      phase,
      /** Exactly `BOARD_SLOT_COUNT` cells; `null` is LOCK-7's explicit empty state. */
      slots,
      capturing: canCapture(match),
      integrity,
      notice,
      detailTile,
    },
    gestures: { onPressStart, onTap, onLongPress, onSwipeLeft, onPressEnd, onActiveChange },
    actions: {
      closeDetail: () => {
        setDetailId(null);
      },
      /** B-15 — a status override committed from the detail sheet; the tiles re-read. */
      reread: () => {
        refresh().catch((cause: unknown) => {
          const message = cause instanceof Error ? cause.message : String(cause);
          setNotice(message);
          log.error(MODULE, 'the board could not be re-read after a detail-sheet write', {
            message,
          });
        });
      },
      dismissNotice: () => {
        setNotice(null);
      },
    },
  };
}

export type LockedBoardGesturesB = ReturnType<typeof useLockedBoardB>['gestures'];
